import * as React from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { DataGrid, GridColDef, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';

import useAxios from '../../utils/useAxios';
import { SectionCard, EmptyState, ConfirmDialog, number } from '../ui';

type SupplierDraft = { name: string; email: string; phone: string; address: string };

const SupplierDialog = ({ supplier, open, onClose, onSaved }: { supplier?: any; open: boolean; onClose: () => void; onSaved: () => void }) => {
  const axios = useAxios();
  const [draft, setDraft] = React.useState<SupplierDraft>({ name: '', email: '', phone: '', address: '' });
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setDraft({ name: supplier?.name ?? '', email: supplier?.email ?? '', phone: supplier?.phone ?? '', address: supplier?.address ?? '' });
      setError('');
    }
  }, [open, supplier]);

  const set = (k: keyof SupplierDraft) => (e: React.ChangeEvent<HTMLInputElement>) => setDraft(d => ({ ...d, [k]: e.target.value }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim()) { setError('El nombre es obligatorio.'); return; }
    const payload = { name: draft.name.trim(), email: draft.email.trim() || null, phone: draft.phone.trim() || null, address: draft.address.trim() || null };
    try {
      if (supplier) await axios.put(`inventory/suppliers/${supplier.id}/`, payload);
      else await axios.post('inventory/suppliers/', payload);
      onSaved();
      onClose();
    } catch (err: any) {
      const data = err?.response?.data;
      setError(typeof data === 'object' && data ? Object.values(data).flat().join(' ') : 'No se pudo guardar el proveedor.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ component: 'form', onSubmit: save } as any}>
      <DialogTitle>{supplier ? 'Editar proveedor' : 'Nuevo proveedor'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid size={12}><TextField label="Nombre" required fullWidth autoFocus value={draft.name} onChange={set('name')} /></Grid>
          <Grid size={{ xs: 12, sm: 6 }}><TextField label="Correo" type="email" fullWidth value={draft.email} onChange={set('email')} helperText="Para enviarle órdenes de compra" /></Grid>
          <Grid size={{ xs: 12, sm: 6 }}><TextField label="Teléfono" type="tel" fullWidth value={draft.phone} onChange={set('phone')} /></Grid>
          <Grid size={12}><TextField label="Dirección" fullWidth value={draft.address} onChange={set('address')} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="inherit" onClick={onClose}>Cancelar</Button>
        <Button type="submit" variant="contained">{supplier ? 'Guardar cambios' : 'Crear proveedor'}</Button>
      </DialogActions>
    </Dialog>
  );
};

const SuppliersTab = () => {
  const axios = useAxios();
  const [suppliers, setSuppliers] = React.useState<any[]>([]);
  const [productCounts, setProductCounts] = React.useState<Record<number, number>>({});
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState<{ supplier?: any } | null>(null);
  const [toDelete, setToDelete] = React.useState<any | null>(null);
  const [refresh, setRefresh] = React.useState(0);

  React.useEffect(() => {
    (async () => {
      const [s, p] = await Promise.allSettled([axios.get('inventory/suppliers/'), axios.get('api/products/')]);
      if (s.status === 'fulfilled') setSuppliers(s.value.data ?? []);
      if (p.status === 'fulfilled') {
        const tally: Record<number, number> = {};
        (p.value.data ?? []).forEach((prod: any) => (prod.suppliers ?? []).forEach((sup: any) => { tally[sup.id] = (tally[sup.id] ?? 0) + 1; }));
        setProductCounts(tally);
      }
      setLoading(false);
    })();
  }, [axios, refresh]);

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Proveedor', flex: 1.2, minWidth: 180, renderCell: ({ value }) => <Box component="span" sx={{ fontWeight: 600 }}>{value}</Box> },
    { field: 'email', headerName: 'Correo', flex: 1.2, minWidth: 180, valueFormatter: v => v || '—' },
    { field: 'phone', headerName: 'Teléfono', flex: 0.8, minWidth: 130, valueFormatter: v => v || '—' },
    { field: 'products', headerName: 'Productos', type: 'number', flex: 0.5, minWidth: 100, valueGetter: (_v, row: any) => productCounts[row.id] ?? 0, valueFormatter: v => number(v) },
    {
      field: 'actions', headerName: '', sortable: false, width: 100, align: 'right',
      renderCell: ({ row }) => (
        <Stack direction="row" sx={{ alignItems: 'center', height: '100%' }}>
          <Tooltip title="Editar"><IconButton size="small" onClick={() => setEditing({ supplier: row })}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Eliminar"><IconButton size="small" onClick={() => setToDelete(row)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <SectionCard noPadding>
        {!loading && suppliers.length === 0 ? (
          <EmptyState
            icon={<LocalShippingOutlinedIcon />}
            title="Registra a tus proveedores"
            description="Asígnalos a tus productos, guarda sus costos y envíales órdenes de compra por correo."
            action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({})}>Agregar proveedor</Button>}
          />
        ) : (
          <DataGrid
            rows={suppliers}
            columns={columns}
            loading={loading}
            autoHeight
            pageSizeOptions={[25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } }, sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }}
            slots={{
              toolbar: () => (
                <Stack direction="row" spacing={1.5} sx={{ px: 2, pt: 2, pb: 1, alignItems: 'center' }}>
                  <GridToolbarQuickFilter placeholder="Buscar proveedor…" debounceMs={200} sx={{ width: { xs: '100%', md: 300 } }} />
                  <Box sx={{ flexGrow: 1 }} />
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({})}>Agregar proveedor</Button>
                </Stack>
              ),
            }}
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          />
        )}
      </SectionCard>

      <SupplierDialog open={!!editing} supplier={editing?.supplier} onClose={() => setEditing(null)} onSaved={() => setRefresh(r => r + 1)} />
      <ConfirmDialog
        open={!!toDelete}
        danger
        title={`¿Eliminar a ${toDelete?.name}?`}
        description="Se quitará de los productos a los que está asignado."
        confirmLabel="Eliminar"
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          await axios.delete(`inventory/suppliers/${toDelete.id}/`);
          setToDelete(null);
          setRefresh(r => r + 1);
        }}
      />
    </>
  );
};

export default SuppliersTab;
